import { motion, AnimatePresence } from "framer-motion";
import { Lock, WhatsappLogo, Sparkle, ArrowRight, X } from "@phosphor-icons/react";

// WhatsApp config
const WHATSAPP_NUMBER = "34641099731";
const WHATSAPP_MESSAGE = "Hola Jordi, quiero información sobre el acompañamiento premium GOATGYM";

/**
 * Premium Lock Screen Component
 * Shows when user tries to access premium-only features
 * 
 * Usage:
 * <PremiumLock 
 *   isOpen={showPremiumLock} 
 *   onClose={() => setShowPremiumLock(false)}
 *   feature="recetas"
 * />
 */
export const PremiumLockScreen = ({ isOpen, onClose, feature = "esta función" }) => {
  const handleWhatsApp = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 backdrop-blur-2xl bg-black/90 flex flex-col items-center justify-center p-6 text-center"
          data-testid="premium-lock-screen"
        >
          {/* Close Button */}
          {onClose && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              onClick={onClose}
              className="absolute top-6 right-6 p-2 text-white/60 hover:text-white transition-colors"
              data-testid="premium-lock-close-btn"
            >
              <X weight="bold" className="w-6 h-6" />
            </motion.button>
          )}

          {/* Lock Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="w-24 h-24 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mb-8"
          >
            <Lock weight="duotone" className="w-12 h-12 text-[#D4AF37]" />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-['Outfit'] text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white mb-4"
          >
            Función <span className="text-[#D4AF37]">Premium</span>
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-[#A3A3A3] text-base sm:text-lg max-w-md mb-4 leading-relaxed"
          >
            {feature.charAt(0).toUpperCase() + feature.slice(1)} forma parte del acompañamiento premium GOATGYM.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-white/80 text-sm sm:text-base max-w-md mb-8"
          >
            Accede a seguimiento individualizado, nutrición personalizada y contacto directo con un entrenador.
          </motion.p>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02, boxShadow: "0 0 40px rgba(212, 175, 55, 0.3)" }}
            whileTap={{ scale: 0.98 }}
            onClick={handleWhatsApp}
            className="group px-8 py-4 bg-[#D4AF37] text-black font-bold text-sm tracking-[0.1em] uppercase hover:bg-[#F0C74A] transition-all duration-300 flex items-center gap-3"
            data-testid="premium-lock-whatsapp-btn"
          >
            <WhatsappLogo weight="fill" className="w-5 h-5" />
            <span>Hablar con un entrenador</span>
          </motion.button>

          {/* Decorative elements */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 0.6 }}
            className="absolute top-1/4 left-1/4 w-32 h-32 bg-[#D4AF37]/5 rounded-full blur-[80px]"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ delay: 0.7 }}
            className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-[100px]"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Premium Banner Component
 * Shows inside the app to promote premium features
 * 
 * Usage:
 * <PremiumBanner />
 */
export const PremiumBanner = ({ className = "" }) => {
  const handleWhatsApp = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`relative overflow-hidden bg-gradient-to-r from-[#141414] to-[#1A1A1A] border border-[#D4AF37]/30 p-5 sm:p-6 ${className}`}
      data-testid="premium-banner"
    >
      {/* Background glow */}
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#D4AF37]/10 rounded-full blur-[60px]" />
      
      <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Content */}
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 bg-[#D4AF37]/10 items-center justify-center flex-shrink-0">
            <Sparkle weight="duotone" className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base sm:text-lg mb-1 flex items-center gap-2">
              <Sparkle weight="duotone" className="w-5 h-5 text-[#D4AF37] sm:hidden" />
              ¿Quieres una transformación real?
            </h3>
            <p className="text-[#A3A3A3] text-sm">
              Accede a seguimiento nutricional personalizado, revisiones semanales y acompañamiento directo para transformar tu físico de verdad.
            </p>
          </div>
        </div>

        {/* CTA */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleWhatsApp}
          className="group w-full sm:w-auto px-5 py-3 bg-[#D4AF37] text-black font-bold text-xs sm:text-sm tracking-wider uppercase hover:bg-[#F0C74A] transition-all duration-300 flex items-center justify-center gap-2 flex-shrink-0"
          data-testid="premium-banner-whatsapp-btn"
        >
          <WhatsappLogo weight="fill" className="w-4 h-4" />
          <span>Contactar</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

/**
 * Premium Feature Card Component
 * Used to show locked premium features inline
 */
export const PremiumFeatureCard = ({ 
  title, 
  description, 
  icon: Icon = Lock,
  onClick 
}) => {
  const handleWhatsApp = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, "_blank");
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01, borderColor: "rgba(212, 175, 55, 0.4)" }}
      className="relative overflow-hidden bg-[#141414] border border-white/10 p-6 cursor-pointer transition-all duration-300"
      onClick={onClick || handleWhatsApp}
      data-testid="premium-feature-card"
    >
      {/* Premium badge */}
      <div className="absolute top-4 right-4">
        <div className="px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-bold tracking-wider uppercase">
          Premium
        </div>
      </div>

      {/* Icon */}
      <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center mb-4">
        <Icon weight="duotone" className="w-6 h-6 text-[#D4AF37]" />
      </div>

      {/* Content */}
      <h4 className="font-['Outfit'] text-lg font-bold text-white mb-2">{title}</h4>
      <p className="text-[#A3A3A3] text-sm mb-4">{description}</p>

      {/* CTA */}
      <div className="flex items-center gap-2 text-[#D4AF37] text-sm font-bold">
        <WhatsappLogo weight="fill" className="w-4 h-4" />
        <span>Contactar para desbloquear</span>
        <ArrowRight weight="bold" className="w-4 h-4" />
      </div>

      {/* Hover glow */}
      <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 via-transparent to-transparent" />
      </div>
    </motion.div>
  );
};

export default PremiumLockScreen;
